import React, { useState } from 'react';
// import { ParsedManuscript } from "../../../../backend/utils/manuscriptUtils/emailParser";

export interface ParsedManuscript {
  manuscriptNumber: string;
  title: string;
  journal: string;
  status: 'Submitted' | 'In Review' | 'Revision Required' | 'Accepted' | 'Published' | 'Unknown';
  date: string;
  threadKey: string;
  parsedBy: 'gpt' | 'none';
  originalSubject?: string;
  from?: string;
  body?: string;
  summary?: string;
}

interface ManuscriptEmail {
  subject: string;
  from: string;
  date: string;
  snippet: string;
  summary?: string;
  body?: string;
}

interface ManuscriptThread {
  title: string;
  latestStatus: string;
  statusColor: string;
  lastUpdated: string;
  from: string;
  threadKey: string;
  history: ManuscriptEmail[];
  latestSummary?: string;
  manualStatus?: ParsedManuscript['status'];
  manualColor?: 'green' | 'yellow' | 'red' | 'gray';
}

interface ManuscriptDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  manuscript: ManuscriptThread;
}

const statusStyle = {
  green: 'bg-green-200 text-green-800',
  yellow: 'bg-yellow-200 text-yellow-800',
  red: 'bg-red-200 text-red-800',
  gray: 'bg-gray-200 text-gray-800',
};

const STATUS_OPTIONS = [
  { label: 'Accepted', value: 'Accepted', color: 'green' },
  { label: 'Published', value: 'Published', color: 'green' },
  { label: 'In Review', value: 'In Review', color: 'yellow' },
  { label: 'Submitted', value: 'Submitted', color: 'red' },
  { label: 'Revision Required', value: 'Revision Required', color: 'red' },
  { label: 'Unknown', value: 'Unknown', color: 'gray' },
];

const ManuscriptDetailsModal: React.FC<ManuscriptDetailsModalProps> = ({
  isOpen,
  onClose,
  manuscript,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(
    manuscript.manualStatus || manuscript.latestStatus
  );
  const [manualColor, setManualColor] = useState(manuscript.manualColor || manuscript.statusColor);
  const [statusUpdated, setStatusUpdated] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<null | boolean>(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const statusToColor = (status: string): 'green' | 'yellow' | 'red' | 'gray' => {
    switch (status) {
      case 'Accepted':
      case 'Published':
        return 'green';
      case 'In Review':
        return 'yellow';
      case 'Submitted':
      case 'Revision Required':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    setManualColor(statusToColor(newStatus));
    setStatusUpdated(true);
    setSaveSuccess(null);
  };

  const handleSave = async () => {
    try {
      const response = await fetch('https://academichub-backend.onrender.com/manuscripts/status', {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          threadKey: manuscript.threadKey,
          status: selectedStatus,
          color: statusToColor(selectedStatus),
        }),
      });

      if (!response.ok) throw new Error('Failed to update status');

      manuscript.manualStatus = selectedStatus as ParsedManuscript['status'];
      manuscript.manualColor = statusToColor(selectedStatus);

      setStatusUpdated(false);
      setSaveSuccess(true);
    } catch (error) {
      console.error('Error updating status:', error);
      setSaveSuccess(false);
    }
  };

  const sortedHistory = [...manuscript.history].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const latestEmail = sortedHistory[0];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-full max-w-3xl p-6 shadow-xl overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{manuscript.title}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-red-500">
            <i className="fa-solid fa-xmark fa-lg"></i>
          </button>
        </div>

        <div className="mb-4 space-y-2">
          <p>
            <strong>Status:</strong>{' '}
            <span
              className={`px-2 py-1 rounded text-sm ${
                statusStyle[manualColor as keyof typeof statusStyle]
              }`}
            >
              {(manuscript.manualStatus || manuscript.latestStatus).replace(/_/g, ' ')}
            </span>
          </p>

          <p>
            <strong>From:</strong> {manuscript.from || 'Unknown'}
          </p>

          <p>
            <strong>Last Updated:</strong> {new Date(manuscript.lastUpdated).toLocaleString()}
          </p>

          <p>
            <strong>Latest Summary:</strong> {manuscript.latestSummary || latestEmail.summary}
          </p>

          <div>
            <strong>Manually update status:</strong>
            <select
              className="ml-2 p-1 border rounded text-sm"
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {statusUpdated && (
              <button
                onClick={handleSave}
                className="ml-3 px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              >
                Save
              </button>
            )}
            {saveSuccess === true && <span className="ml-2 text-green-600 text-sm">✔️ Saved</span>}
            {saveSuccess === false && (
              <span className="ml-2 text-red-600 text-sm">❌ Save failed</span>
            )}
          </div>
        </div>

        {latestEmail?.body && (
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-2">Latest Email Body:</h3>
            <details className="bg-gray-100 p-3 rounded text-sm text-gray-800 whitespace-pre-wrap">
              <summary className="cursor-pointer text-blue-500">View latest body</summary>
              <p className="mt-2">{latestEmail.body}</p>
            </details>
          </div>
        )}

        <div className="mb-6">
          <h3 className="font-semibold text-lg mb-2">Email History:</h3>
          <ul className="space-y-4">
            {sortedHistory.map((email, i) => (
              <div key={i} className="mb-4 border-b pb-2">
                <p className="text-sm font-semibold">{email.subject}</p>
                {email.summary && (
                  <p className="text-sm italic text-gray-700 mt-1">
                    <strong>Summary</strong>: {email.summary}
                  </p>
                )}
                <p className="text-sm italic text-gray-700 mt-1">
                  <strong>Date:</strong> {new Date(email.date).toLocaleString()}
                </p>
                <p className="text-sm mt-1 text-gray-700">{email.snippet}</p>
                {email.body && (
                  <details className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">
                    <summary className="cursor-pointer text-blue-500">View full body</summary>
                    <p className="mt-1">{email.body}</p>
                  </details>
                )}
              </div>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManuscriptDetailsModal;
